import { Injectable } from '@angular/core';

import { AuthCoreService } from './auth-core.service';
import { UserRole, RolePermissions } from '../interfaces/auth.interface';

/**
 * Service for role-based authorization
 * Handles permission checking and role management
 */
@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {

  constructor(private authService: AuthCoreService) {}

  /**
   * Gets the current user's roles
   */
  getCurrentUserRoles(): UserRole[] {
    return this.authService.getCurrentUserRoles() as UserRole[];
  }

  /**
   * Gets the current user's primary role (first role)
   */
  getCurrentUserRole(): UserRole | null {
    return this.authService.getCurrentUserRole() as UserRole | null;
  }

  /**
   * Checks if the user has a specific role
   */
  hasRole(role: UserRole): boolean {
    return this.authService.hasRole(role);
  }

  /**
   * Checks if the user has any of the specified roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    return this.authService.hasAnyRole(roles);
  }

  /**
   * Gets role-based permissions for the current user
   * Multiple roles: permissions are merged (union of all role permissions)
   */
  getCurrentUserPermissions(): RolePermissions {
    const roles = this.getCurrentUserRoles();
    
    if (!roles || roles.length === 0) {
      return this.getNoPermissions();
    }

    // Több role esetén a jogosultságok uniója
    const merged = this.getNoPermissions();
    for (const role of roles) {
      const perms = this.getRolePermissions(role);
      for (const key of Object.keys(merged) as (keyof RolePermissions)[]) {
        if (perms[key]) {
          (merged as any)[key] = true;
        }
      }
    }
    return merged;
  }

  /**
   * Gets permissions for a specific role
   */
  getRolePermissions(role: UserRole): RolePermissions {
    switch (role) {
      case 'admin':
        return {
          canViewAdminPanel: true,
          canManageUsers: true,
          canManageJobTitles: true,
          canManageInsuranceCompanies: true,
          canManageProducts: true,
          canViewCallCenterStats: true,
          canEditCallCenterStats: true,
          canViewAllCustomers: true,
          canEditCustomers: true,
          canViewContracts: true,
          canEditContracts: true,
        };

      case 'sales_manager':
      case 'deputy-CEO':
        return {
          canViewAdminPanel: false,
          canManageUsers: false,
          canManageJobTitles: false,
          canManageInsuranceCompanies: true,
          canManageProducts: true,
          canViewCallCenterStats: true,
          canEditCallCenterStats: true,
          canViewAllCustomers: true,
          canEditCustomers: true,
          canViewContracts: true,
          canEditContracts: true,
        };

      case 'call_center':
        return {
          canViewAdminPanel: false,
          canManageUsers: false,
          canManageJobTitles: false,
          canManageInsuranceCompanies: false,
          canManageProducts: false,
          canViewCallCenterStats: true,
          canEditCallCenterStats: true,
          canViewAllCustomers: true,
          canEditCustomers: true,
          canViewContracts: false,
          canEditContracts: false,
        };

      case 'kam':
        return {
          canViewAdminPanel: false,
          canManageUsers: false,
          canManageJobTitles: false,
          canManageInsuranceCompanies: false,
          canManageProducts: false,
          canViewCallCenterStats: false,
          canEditCallCenterStats: false,
          canViewAllCustomers: true,
          canEditCustomers: true,
          canViewContracts: false,
          canEditContracts: false,
        };

      case 'investment':
        return {
          canViewAdminPanel: false,
          canManageUsers: false,
          canManageJobTitles: false,
          canManageInsuranceCompanies: false,
          canManageProducts: false,
          canViewCallCenterStats: false,
          canEditCallCenterStats: false,
          canViewAllCustomers: true,
          canEditCustomers: false,
          canViewContracts: true,
          canEditContracts: false,
        };

      default:
        return this.getNoPermissions();
    }
  }

  /**
   * Returns a permission object with all permissions set to false
   */
  private getNoPermissions(): RolePermissions {
    return {
      canViewAdminPanel: false,
      canManageUsers: false,
      canManageJobTitles: false,
      canManageInsuranceCompanies: false,
      canManageProducts: false,
      canViewCallCenterStats: false,
      canEditCallCenterStats: false,
      canViewAllCustomers: false,
      canEditCustomers: false,
      canViewContracts: false,
      canEditContracts: false,
    };
  }

  /**
   * Convenience methods for common role checks
   */
  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  isManager(): boolean {
    return this.authService.isManager();
  }

  isCallCenterEmployee(): boolean {
    return this.authService.isCallCenterEmployee();
  }

  isKAM(): boolean {
    return this.authService.isKAM();
  }

  isInvestmentDivision(): boolean {
    return this.authService.isInvestmentDivision();
  }

  isSalesSupportManager(): boolean {
    return this.authService.isSalesSupportManager();
  }

  /**
   * Checks if the user can access the admin panel
   */
  canAccessAdminPanel(): boolean {
    return this.getCurrentUserPermissions().canViewAdminPanel;
  }

  /**
   * Checks if the user can manage other users
   */
  canManageUsers(): boolean {
    return this.getCurrentUserPermissions().canManageUsers;
  }

  /**
   * Checks if the user can view and edit customer data
   */
  canManageCustomers(): boolean {
    return this.getCurrentUserPermissions().canEditCustomers;
  }

  /**
   * Checks if the user can view and edit contracts
   */
  canManageContracts(): boolean {
    return this.getCurrentUserPermissions().canEditContracts;
  }
}